"use client";

import { createTranslator, type TranslationValues } from "@eg/shared";
import { createContext, useCallback, useContext, useMemo } from "react";
import { useRouter } from "next/navigation";
import { localeCookieName, type Locale } from "./config";
import type { WebMessageKey, WebMessages } from "./messages";

type I18nContextValue = {
  locale: Locale;
  t: (key: WebMessageKey, values?: TranslationValues) => string;
  setLocale: (locale: Locale) => void;
};

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({
  locale,
  messages,
  children,
}: {
  locale: Locale;
  messages: WebMessages;
  children: React.ReactNode;
}) {
  const router = useRouter();
  const t = useMemo(() => createTranslator(messages), [messages]);

  const setLocale = useCallback(
    (nextLocale: Locale) => {
      if (nextLocale === locale) return;
      document.cookie = `${localeCookieName}=${nextLocale}; Path=/; Max-Age=31536000; SameSite=Lax`;
      router.refresh();
    },
    [locale, router],
  );

  const value = useMemo(
    () => ({ locale, t, setLocale }),
    [locale, setLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n must be used within <I18nProvider />");
  return value;
}

export function useTranslations() {
  return useI18n().t;
}

export function useLocale() {
  return useI18n().locale;
}

export function useSetLocale() {
  return useI18n().setLocale;
}
